import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CompetitionAPI } from "../../../services/api";
import DashboardLayout from "../../../components/layouts/DashboardLayout";
import Countdown from "../../../components/common/Countdown";
import iconLogo from "../../../assets/icon-logo.png";
import banner1 from "../../../assets/banner-1.png";
import { COMPETITION_YEAR } from "../../../config/competition";

// Language translations - for future multilanguage support
const translations = {
  en: {
    // Phase labels
    viewBrief: "View Brief",
    submitEntry: "Submit Entry",
    waitingForBrief: "Waiting for brief",
    briefAvailable: "Brief available",
    briefAccessEnded: "Brief access ended",
    waitingToSubmit: "Waiting to submit",
    submissionOpen: "Submission open",
    submissionClosed: "Submission closed",
    notConfigured: "Not configured",
    // Countdown labels
    opensIn: "Opens in:",
    timeLeft: "Time left:",
    // Time labels
    start: "Start:",
    end: "End:",
    // Status messages
    timeExpired: "Time expired",
    submitted: "Submitted",
    acceptingSubmissions: "Accepting submissions",
    // Card status
    notEligible: "NOT ELIGIBLE",
    congratulations: "CONGRATULATIONS",
    submissionCompleted: "Submission Completed",
    competitionStatus: "COMPETITION STATUS",
    readyToCompete: "Ready to compete",
    // Buttons
    viewCompetition: "View Competition",
    notAvailable: "Not Available",
    goToTeamDashboard: "Go to Team Dashboard",
    retry: "Retry",
    // Empty state
    noCompetitionsFound: "No Competitions Found",
    noCompetitionsDesc:
      "You don't have any eligible teams for competitions yet.",
    completeRegistration:
      "Complete your team registration and payment to participate.",
    // Tips
    tipsTitle: "Tips for a better adventure",
  },
  vi: {
    // Phase labels
    viewBrief: "Xem Đề Thi",
    submitEntry: "Nộp Bài Thi",
    waitingForBrief: "Chờ ra đề",
    briefAvailable: "Đang mở đề",
    briefAccessEnded: "Đã hết hạn xem đề",
    waitingToSubmit: "Chờ mở nộp bài",
    submissionOpen: "Đang mở nộp bài",
    submissionClosed: "Đã hết hạn nộp bài",
    notConfigured: "Chưa cấu hình",
    // Countdown labels
    opensIn: "Mở sau:",
    timeLeft: "Còn lại:",
    // Time labels
    start: "Bắt đầu:",
    end: "Kết thúc:",
    // Status messages
    timeExpired: "Đã hết thời gian",
    submitted: "Đã nộp bài",
    acceptingSubmissions: "Đang mở nhận bài",
    // Card status
    notEligible: "KHÔNG ĐỦ ĐIỀU KIỆN",
    congratulations: "CHÚC MỪNG",
    submissionCompleted: "Đã Nộp Bài",
    competitionStatus: "TRẠNG THÁI",
    readyToCompete: "Sẵn sàng thi đấu",
    // Buttons
    viewCompetition: "Xem Chi Tiết",
    notAvailable: "Không Khả Dụng",
    goToTeamDashboard: "Đi đến Quản lý Team",
    retry: "Thử lại",
    // Empty state
    noCompetitionsFound: "Không Tìm Thấy Cuộc Thi",
    noCompetitionsDesc: "Bạn chưa có team đủ điều kiện tham gia cuộc thi.",
    completeRegistration: "Hoàn thành đăng ký team và thanh toán để tham gia.",
    // Tips
    tipsTitle: "Mẹo cho cuộc phiêu lưu tốt hơn",
  },
};

// Current language - change to 'vi' for Vietnamese
const currentLang: "en" | "vi" = "en";
const t = translations[currentLang];

// Types
interface Team {
  id: number;
  team_id: string;
  team_name: string;
  category: {
    id: number;
    name: string;
  };
  league: {
    id: number;
    name: string;
  };
}

interface PhaseInfo {
  status: "waiting" | "active" | "closed" | "not_configured";
  is_active: boolean;
  start_time: string | null;
  end_time: string | null;
  countdown_to: string | null;
  has_brief?: boolean;
}

interface Competition {
  team: Team;
  is_eligible: boolean;
  eligibility_reasons: string[];
  contest_access: PhaseInfo | null;
  submission_window: PhaseInfo | null;
  submission: {
    id: number;
    status: string;
    has_thumbnail: boolean;
    has_pdf: boolean;
    has_video: boolean;
    has_consent: boolean;
    big_idea: string | null;
    submitted_at: string | null;
  } | null;
}

interface ApiResponse {
  success: boolean;
  data: {
    competitions: Competition[];
    schedule: any;
  };
}

const CompetitionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState<Competition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  // Banner carousel data
  const banners = [
    {
      id: 1,
      image: banner1,
      title: `Vietnam Young Lions ${COMPETITION_YEAR}`,
      subtitle: "Unleash Your Creative Potential",
    },
    {
      id: 2,
      image: banner1,
      title: "Join the Competition",
      subtitle: "Make Your Mark in the Industry",
    },
    {
      id: 3,
      image: banner1,
      title: "Creative Excellence",
      subtitle: "Where Ideas Come to Life",
    },
  ];

  // Auto-advance banner carousel every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [banners.length]);

  useEffect(() => {
    fetchCompetitions();
  }, [refreshKey]);

  const fetchCompetitions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: ApiResponse = await CompetitionAPI.getMyCompetitions();

      if (response.success && response.data) {
        setCompetitions(response.data.competitions || []);
      } else {
        setError("Failed to load competitions");
      }
    } catch (err: any) {
      console.error("Error fetching competitions:", err);
      setError(err.message || "Failed to load competitions");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const handleViewCompetition = (teamId: number) => {
    navigate(`/dashboard/competitions/${teamId}`);
  };

  // Get phase display info
  const getPhaseDisplay = (
    phase: PhaseInfo | null,
    type: "brief" | "submission",
  ) => {
    if (!phase) {
      return {
        label: t.notConfigured,
        color: "gray",
      };
    }

    const labels = {
      brief: {
        waiting: t.waitingForBrief,
        active: t.briefAvailable,
        closed: t.briefAccessEnded,
      },
      submission: {
        waiting: t.waitingToSubmit,
        active: t.submissionOpen,
        closed: t.submissionClosed,
      },
    };

    return {
      label:
        labels[type][phase.status as keyof (typeof labels)["brief"]] ||
        phase.status,
      color:
        phase.status === "active"
          ? "green"
          : phase.status === "waiting"
            ? "yellow"
            : "gray",
    };
  };

  // Tips content
  const tips = {
    title: t.tipsTitle,
    items: [
      "Make sure to download the brief as soon as it becomes available.",
      "Read all requirements carefully before starting your submission.",
      "Submit your work before the deadline - late submissions won't be accepted.",
      "Check all file formats and sizes match the requirements.",
      "Keep your team communication active throughout the competition.",
    ],
  };

  // Competition card component
  const CompetitionCard = ({ competition }: { competition: Competition }) => {
    const briefDisplay = getPhaseDisplay(competition.contest_access, "brief");
    const submissionDisplay = getPhaseDisplay(
      competition.submission_window,
      "submission",
    );

    return (
      <div
        className={`bg-white border-2 border-[#262423] rounded-lg p-4 sm:p-6 mb-4 shadow-sm transition-shadow ${
          competition.is_eligible
            ? "hover:shadow-md cursor-pointer"
            : "opacity-75"
        }`}
        onClick={() =>
          competition.is_eligible && handleViewCompetition(competition.team.id)
        }
      >
        {/* Two Column Layout - Responsive */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-4">
          {/* Left Column - Team Info - 40% on desktop */}
          <div className="w-full sm:w-2/5 sm:pr-6 text-left">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
              {competition.team.team_name}
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 font-mono mb-3 sm:mb-4 tracking-wider">
              {competition.team.team_id}
            </p>
            <hr className="border-gray-300 mb-3 sm:mb-4 w-full sm:w-1/2" />

            {/* Category & League */}
            <div className="space-y-2 mb-4">
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700">
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                <span className="truncate">
                  {competition.team.category.name}
                </span>
              </div>
              <div className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700">
                <svg
                  className="w-3 h-3 sm:w-4 sm:h-4 text-gray-500 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                <span className="truncate">{competition.team.league.name}</span>
              </div>
            </div>

            {/* Status */}
            <div className="mt-auto pt-2">
              {!competition.is_eligible ? (
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-orange-600">
                    {t.notEligible}
                  </div>
                  <div className="text-xs text-orange-500">
                    {competition.eligibility_reasons.join(", ")}
                  </div>
                </div>
              ) : competition.submission?.status === "submitted" ? (
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-[#646464]">
                    {t.congratulations}
                  </div>
                  <div className="text-xs text-green-600">
                    {t.submissionCompleted}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-[#646464]">
                    {t.competitionStatus}
                  </div>
                  <div className="text-xs text-blue-600">
                    {t.readyToCompete}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Phase Status - 60% on desktop */}
          <div className="w-full sm:w-3/5 text-left">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Contest Access (Brief) Phase */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-center space-y-3">
                  <div className="w-10 h-10 mx-auto bg-blue-50 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">
                      {t.viewBrief}
                    </h4>
                    <span
                      className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        competition.contest_access?.status === "active"
                          ? "bg-green-100 text-green-800"
                          : competition.contest_access?.status === "waiting"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {briefDisplay.label}
                    </span>
                  </div>

                  {competition.contest_access ? (
                    <>
                      {/* Hide countdown if already submitted */}
                      {competition.contest_access.countdown_to &&
                        competition.submission?.status !== "submitted" && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">
                              {competition.contest_access.status === "waiting"
                                ? t.opensIn
                                : t.timeLeft}
                            </p>
                            <Countdown
                              targetDate={
                                competition.contest_access.countdown_to
                              }
                              variant="badge"
                              size="lg"
                              onComplete={handleRefresh}
                            />
                          </div>
                        )}

                      <div className="text-xs text-gray-500 space-y-0.5">
                        <p>
                          {t.start}{" "}
                          {formatDate(competition.contest_access.start_time)}
                        </p>
                        <p>
                          {t.end}{" "}
                          {formatDate(competition.contest_access.end_time)}
                        </p>
                      </div>

                      {competition.contest_access.status === "closed" &&
                        competition.submission?.status !== "submitted" && (
                          <p className="text-xs text-red-600 font-medium">
                            {t.timeExpired}
                          </p>
                        )}
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">{t.notConfigured}</p>
                  )}
                </div>
              </div>

              {/* Submission Window Phase */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <div className="text-center space-y-3">
                  <div className="w-10 h-10 mx-auto bg-green-50 rounded-full flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-green-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                      />
                    </svg>
                  </div>

                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">
                      {t.submitEntry}
                    </h4>
                    <span
                      className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        competition.submission_window?.status === "active"
                          ? "bg-green-100 text-green-800"
                          : competition.submission_window?.status === "waiting"
                            ? "bg-yellow-100 text-yellow-800"
                            : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {submissionDisplay.label}
                    </span>
                  </div>

                  {competition.submission_window ? (
                    <>
                      {/* Hide countdown if already submitted */}
                      {competition.submission_window.countdown_to &&
                        competition.submission?.status !== "submitted" && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">
                              {competition.submission_window.status ===
                              "waiting"
                                ? t.opensIn
                                : t.timeLeft}
                            </p>
                            <Countdown
                              targetDate={
                                competition.submission_window.countdown_to
                              }
                              variant="badge"
                              size="lg"
                              onComplete={handleRefresh}
                            />
                          </div>
                        )}

                      {/* Hide time info if already submitted */}
                      {competition.submission?.status !== "submitted" && (
                        <div className="text-xs text-gray-500 space-y-0.5">
                          <p>
                            {t.start}{" "}
                            {formatDate(
                              competition.submission_window.start_time,
                            )}
                          </p>
                          <p>
                            {t.end}{" "}
                            {formatDate(competition.submission_window.end_time)}
                          </p>
                        </div>
                      )}

                      {competition.submission?.status === "submitted" ? (
                        <p className="text-xs text-green-600 font-medium">
                          {t.submitted}
                        </p>
                      ) : competition.submission_window.status === "closed" ? (
                        <p className="text-xs text-red-600 font-medium">
                          {t.timeExpired}
                        </p>
                      ) : competition.submission_window.status === "active" ? (
                        <p className="text-xs text-green-600 font-medium">
                          {t.acceptingSubmissions}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-xs text-gray-400">{t.notConfigured}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Button */}
        <div className="flex justify-end pt-3 sm:pt-4 border-t border-gray-100">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (competition.is_eligible) {
                handleViewCompetition(competition.team.id);
              }
            }}
            disabled={!competition.is_eligible}
            className={`py-2 px-4 sm:px-6 rounded-lg transition-all font-medium text-xs sm:text-sm min-w-[120px] sm:min-w-[140px] ${
              competition.is_eligible
                ? "text-[#403634] hover:text-[#2d2520] hover:bg-gray-50 border border-[#403634] hover:border-[#2d2520]"
                : "text-gray-400 bg-gray-100 cursor-not-allowed border border-gray-200"
            }`}
          >
            {competition.is_eligible ? t.viewCompetition : t.notAvailable}
          </button>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="flex flex-col lg:flex-row gap-8 bg-[#EDEDED] rounded-lg p-4 sm:p-6 lg:p-8 shadow-sm">
        {/* Main Content */}
        <div className="flex-1">
          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-6">
              <p>{error}</p>
              <button
                onClick={handleRefresh}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                {t.retry}
              </button>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && competitions.length === 0 && (
            <div className="text-center py-12">
              <img
                src={iconLogo}
                alt="VYL Logo"
                className="w-16 h-16 mx-auto mb-4 opacity-50"
              />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t.noCompetitionsFound}
              </h3>
              <p className="text-gray-600 mb-6">
                {t.noCompetitionsDesc}
                <br />
                {t.completeRegistration}
              </p>
              <button
                onClick={() => navigate("/dashboard/team")}
                className="bg-gradient-to-b from-[#403634] to-[#1E1E1E] text-white py-3 px-6 rounded-lg hover:opacity-90 transition-opacity font-medium"
              >
                {t.goToTeamDashboard}
              </button>
            </div>
          )}

          {/* Competitions List */}
          {!loading && !error && competitions.length > 0 && (
            <div className="space-y-4">
              {competitions.map((competition) => (
                <CompetitionCard
                  key={competition.team.id}
                  competition={competition}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Sidebar - Tips and Promotional */}
        <div className="hidden lg:block w-80 space-y-4">
          {/* Banner Carousel */}
          <div className="bg-white rounded-lg p-6 shadow-sm">
            <div className="rounded-lg text-white relative overflow-hidden mb-5">
              <div className="relative w-full h-48">
                {banners.map((banner, index) => (
                  <div
                    key={banner.id}
                    className={`absolute inset-0 transition-opacity duration-500 ease-in-out ${
                      index === currentBannerIndex ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    <img
                      src={banner.image}
                      alt={banner.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                {/* Carousel Indicators */}
                <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex space-x-1">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentBannerIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentBannerIndex
                          ? "bg-white"
                          : "bg-white bg-opacity-50"
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Tips Section */}
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {tips.title}
            </h3>
            <div className="space-y-2 text-xs text-gray-600">
              {tips.items.map((tip, index) => (
                <p key={index}>{tip}</p>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CompetitionsPage;
